import { useState, type ReactNode } from 'react';
import type { MargensGrupoLinha } from '@triade/contracts';
import { useMargensGrupo, type RecorteMargens } from '@/api/margens';
import { ProfitBadge } from '@/components/profit-badge';
import { formatarContagem, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { Bloco } from './bloco';
import { Paginacao } from './paginacao';
import { TabelaOrdenavel, type ColunaMargem, type Direcao } from './tabela-ordenavel';
import type { BuscaMargens } from './tipos';
import { paraConsulta } from './tipos';

export interface SecaoGrupoProps {
  id: string;
  titulo: string;
  descricao: string;
  recorte: RecorteMargens;
  busca: BuscaMargens;
  /** Rotulo da primeira coluna e da contagem. */
  colunaRotulo: string;
  colunaQuantidade: string;
  /** Primeira celula: normalmente um link que leva a lista filtrada (drill-down). */
  rotulo: (linha: MargensGrupoLinha) => ReactNode;
  vazio: string;
  limit?: number;
}

type Campo = 'rotulo' | 'quantidade' | 'receita' | 'lucroMedio' | 'mcPct' | 'abaixoDaMeta';

/** Quadro de um recorte (tabela, cliente, produto, tipo): tabela ordenavel pelo servidor, paginada. */
export function SecaoGrupo({ id, titulo, descricao, recorte, busca, colunaRotulo, colunaQuantidade, rotulo, vazio, limit = 10 }: SecaoGrupoProps) {
  const [ordenar, setOrdenar] = useState<Campo>('lucroMedio');
  const [direcao, setDirecao] = useState<Direcao>('asc');
  const [offset, setOffset] = useState(0);
  const consulta = useMargensGrupo(recorte, { ...paraConsulta(busca), ordenar, direcao, limit, offset });
  const dados = consulta.data;

  const ordenarPor = (campo: string) => {
    setOffset(0);
    if (campo === ordenar) setDirecao(direcao === 'asc' ? 'desc' : 'asc');
    else {
      setOrdenar(campo as Campo);
      setDirecao(campo === 'rotulo' ? 'asc' : campo === 'lucroMedio' ? 'asc' : 'desc');
    }
  };

  const colunas: ColunaMargem<MargensGrupoLinha>[] = [
    { id: 'rotulo', rotulo: colunaRotulo, campo: 'rotulo', celula: rotulo },
    { id: 'quantidade', rotulo: colunaQuantidade, campo: 'quantidade', direita: true, mono: true, celula: (g) => formatarContagem(g.quantidade) },
    { id: 'receita', rotulo: 'Receita', campo: 'receita', direita: true, mono: true, celula: (g) => formatarMoeda(g.exibicao.receita) },
    {
      id: 'lucroMedio',
      rotulo: 'Lucro médio',
      campo: 'lucroMedio',
      direita: true,
      celula: (g) =>
        g.exibicao.lucroMedio === null ? (
          '—'
        ) : (
          <ProfitBadge lucro={g.exibicao.lucroMedio} faixa={g.faixa ?? undefined} meta={g.exibicao.metaLucro ?? '0'} minimo={g.exibicao.minimoLucro ?? '0'} compacto />
        ),
    },
    // Ordenavel pelo servidor (ordenar=mcPct); sem MC gravada (versao antiga) mostra "—".
    { id: 'mcPct', rotulo: 'MC%', campo: 'mcPct', direita: true, mono: true, celula: (g) => formatarFracaoPercentual(g.exibicao.mcPct) },
    { id: 'abaixoDaMeta', rotulo: 'Abaixo da meta', campo: 'abaixoDaMeta', direita: true, mono: true, celula: (g) => formatarContagem(g.abaixoDaMeta) },
    { id: 'abaixoDoMinimo', rotulo: 'Abaixo do mínimo', direita: true, mono: true, celula: (g) => formatarContagem(g.abaixoDoMinimo) },
  ];

  return (
    <section id={id} aria-label={titulo} aria-busy={consulta.isFetching} className="rounded-2xl border border-border bg-card px-5 py-[18px]" data-testid={`secao-${recorte}`}>
      <h2 className="m-0 text-title font-semibold">{titulo}</h2>
      <div className="mb-3.5 text-small text-ink-muted">{descricao}</div>
      <Bloco
        carregando={consulta.isFetching}
        erro={consulta.error}
        temDados={!!dados}
        vazio={dados?.total === 0}
        mensagemVazia={vazio}
        textoCarregando="Carregando…"
        onTentar={() => void consulta.refetch()}
      >
        {dados && (
          <div className="flex flex-col gap-3">
            <TabelaOrdenavel
              legenda={titulo}
              linhas={dados.itens}
              colunas={colunas}
              ordenar={ordenar}
              direcao={direcao}
              onOrdenar={ordenarPor}
              chave={(g) => g.chave || 'sem'}
              testIdLinha={`linha-${recorte}`}
            />
            <Paginacao offset={dados.offset} limit={limit} quantidade={dados.itens.length} total={dados.total} onMudar={setOffset} testId={`contagem-${recorte}`} />
          </div>
        )}
      </Bloco>
    </section>
  );
}
